import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../api/axios'
import {
  HomeIcon,
  EyeIcon,
  UsersIcon,
  UserGroupIcon,
  KeyIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  ServerStackIcon,
  BuildingOfficeIcon,
  FlagIcon,
  RectangleStackIcon,
  NewspaperIcon,
  DevicePhoneMobileIcon,
  CurrencyDollarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  ClockIcon,
  CircleStackIcon,
  CommandLineIcon,
  ArrowDownTrayIcon,
  EnvelopeOpenIcon,
  PaperAirplaneIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  BugAntIcon,
} from '@heroicons/react/24/outline'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
  badgeColor?: 'amber' | 'red' | 'blue'
}

interface NavSection {
  title: string
  items: NavItem[]
}

interface SidebarProps {
  onClose?: () => void
}

export default function Sidebar({ onClose }: SidebarProps) {
  const { logout, user } = useAuth()
  const location = useLocation()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [trialCount, setTrialCount] = useState<number>(0)
  const [pendingCount, setPendingCount] = useState<number>(0)
  const [alertCount, setAlertCount] = useState<number>(0)
  const [reviewCount, setReviewCount] = useState<number>(0)

  useEffect(() => {
    fetchBadgeCounts()
  }, [])

  const fetchBadgeCounts = async () => {
    try {
      const res = await api.get('/super-admin/dashboard/stats')
      const stats = res.data
      setTrialCount(stats.trialCompanies ?? 0)
      setAlertCount(stats.activeAlerts ?? 0)
      // Pending = trials without stack auth configured
      if (stats.pendingCompanies !== undefined) {
        setPendingCount(stats.pendingCompanies)
      }
      api.get('/super-admin/signups')
        .then((r) => setReviewCount(r.data?.counts?.review ?? 0))
        .catch(() => { /* badge only */ })
    } catch {
      // Silently fail — badges are a nice-to-have
    }
  }

  const navigationSections: NavSection[] = [
    {
      title: 'Overview',
      items: [
        { path: '/', label: 'Dashboard', icon: HomeIcon },
      ],
    },
    {
      title: 'Companies',
      items: [
        {
          path: '/companies',
          label: 'Companies',
          icon: BuildingOfficeIcon,
          badge: pendingCount > 0 ? pendingCount : undefined,
          badgeColor: 'amber',
        },
        {
          path: '/signups',
          label: 'Signups & trials',
          icon: ClockIcon,
          // Signups waiting on a decision matter more than the trial count.
          badge: reviewCount > 0 ? reviewCount : trialCount > 0 ? trialCount : undefined,
          badgeColor: 'amber',
        },
        { path: '/plans', label: 'Plans', icon: RectangleStackIcon },
        { path: '/features', label: 'Company Features', icon: FlagIcon },
      ],
    },
    {
      title: 'Monitoring',
      items: [
        {
          path: '/monitoring',
          label: 'System Monitoring',
          icon: EyeIcon,
          badge: alertCount > 0 ? alertCount : undefined,
          badgeColor: 'red',
        },
        { path: '/reports', label: 'Reports & Analytics', icon: DocumentChartBarIcon },
        { path: '/audit-logs', label: 'Audit Logs', icon: ClipboardDocumentListIcon },
        { path: '/error-reports', label: 'Error Reports', icon: BugAntIcon },
      ],
    },
    {
      title: 'Admin',
      items: [
        // 'User Management' is OPERATOR accounts for this portal
        // (registry_super_admin_users). 'Tenant Accounts' is the people who sign
        // in to customer portals. Two different populations that were previously
        // split across this portal and the Zitadel console — labelled explicitly
        // because "users" alone does not distinguish them.
        { path: '/users', label: 'Operator Accounts', icon: UsersIcon },
        { path: '/tenant-accounts', label: 'Tenant Accounts', icon: UserGroupIcon },
        { path: '/auth-migration', label: 'Auth Migration', icon: KeyIcon },
        { path: '/announcements', label: 'News', icon: NewspaperIcon },
        { path: '/quickbooks', label: 'QuickBooks', icon: CurrencyDollarIcon },
      ],
    },
    {
      title: 'Tools',
      items: [
        // One destination for everything that acts on a database. Schema
        // Templates, Backups and Data Retention used to be three more entries
        // across two nav groups, so answering "is this migration applied, and
        // do I have a backup before I run it?" meant visiting three pages.
        // They are tabs of /database now; their old paths redirect.
        //
        // Storage Config deliberately stays out: it configures per-company FILE
        // storage (S3/SharePoint/RustFS for uploads), not database storage.
        { path: '/database', label: 'Database', icon: ServerStackIcon },
        { path: '/email-templates', label: 'Email Templates', icon: EnvelopeOpenIcon },
        { path: '/email-notification-test', label: 'Email Notification Test', icon: PaperAirplaneIcon },
        { path: '/data-export', label: 'Data Export', icon: ArrowDownTrayIcon },
        { path: '/server-logs', label: 'Server Logs', icon: CommandLineIcon },
        { path: '/storage-config', label: 'Storage Config', icon: CircleStackIcon },
        { path: '/app-versions', label: 'App Fleet', icon: DevicePhoneMobileIcon },
        { path: '/maintenance', label: 'Maintenance & Repairs', icon: WrenchScrewdriverIcon },
      ],
    },
    {
      title: 'System',
      items: [
        { path: '/settings', label: 'Settings', icon: Cog6ToothIcon },
      ],
    },
  ]

  const isActive = (itemPath: string) => {
    if (itemPath === '/') return location.pathname === '/'
    return location.pathname.startsWith(itemPath)
  }

  const handleNavClick = () => {
    onClose?.()
  }

  return (
    <div className={`relative h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-slate-100 transition-all duration-300 ease-out flex flex-col shadow-2xl ${isCollapsed ? 'w-20' : 'w-64'}`}>
      {/* Header */}
      <div className="relative border-b border-slate-700/50 bg-slate-900/80 backdrop-blur px-4 py-4">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/30">
                <span className="text-white font-bold text-xs">SA</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">Super Admin</div>
                <div className="text-xs text-slate-400 truncate">{user?.email || user?.name}</div>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto shadow-lg shadow-blue-500/30">
              <span className="text-white font-bold text-xs">SA</span>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors flex-shrink-0 ${isCollapsed ? 'absolute -right-3 top-4 bg-slate-800 border border-slate-700 shadow-lg' : 'ml-2'}`}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400" />
            ) : (
              <ChevronLeftIcon className="w-3.5 h-3.5 text-slate-400" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2.5 space-y-5 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
        {navigationSections.map((section) => (
          <div key={section.title}>
            {!isCollapsed && (
              <div className="px-2.5 py-1.5 mb-1">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{section.title}</div>
              </div>
            )}
            {isCollapsed && <div className="my-2 mx-2 h-px bg-slate-700/50" />}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon
                const active = isActive(item.path)
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={handleNavClick}
                      title={isCollapsed ? item.label : undefined}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-150 group relative ${
                        active
                          ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/25'
                          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                      }`}
                    >
                      {!active && (
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-600/0 via-blue-500/0 to-blue-600/0 group-hover:from-blue-600/10 group-hover:via-blue-500/15 group-hover:to-blue-600/10 transition-all duration-150" />
                      )}

                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform duration-150 ${active ? 'scale-110' : 'group-hover:scale-105'}`} />

                      {!isCollapsed && (
                        <span className="flex-1 text-sm font-medium truncate relative z-10">{item.label}</span>
                      )}

                      {/* Badge */}
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className={[
                          'inline-flex items-center justify-center rounded-full text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex-shrink-0 relative z-10',
                          active
                            ? 'bg-white/25 text-white'
                            : item.badgeColor === 'red'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700',
                        ].join(' ')}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}

                      {/* Active indicator (collapsed) */}
                      {active && isCollapsed && (
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-blue-400 rounded-l-lg" />
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={`border-t border-slate-700/50 bg-slate-900/80 backdrop-blur p-2.5 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
        <button
          onClick={logout}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-red-500/20 transition-all duration-150 group relative overflow-hidden text-left ${isCollapsed ? 'justify-center' : ''}`}
          title="Sign out"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-500/0 to-red-600/0 group-hover:from-red-600/10 group-hover:via-red-500/15 group-hover:to-red-600/10 transition-all duration-150" />
          <ArrowRightOnRectangleIcon className="w-[18px] h-[18px] flex-shrink-0 transition-transform duration-150 group-hover:scale-105" />
          {!isCollapsed && <span className="text-sm font-medium relative z-10">Sign Out</span>}
        </button>
      </div>
    </div>
  )
}
